import { Box, BoxProps, Button, css, lighten, Link, linkClasses, styled, Typography } from '@mui/material';
import { grey } from '@mui/material/colors';
import { useMemoizedFn } from 'ahooks';
import React, { ReactElement, useEffect } from 'react';
import { Accept, ErrorCode, useDropzone } from 'react-dropzone';
import toast from 'react-hot-toast';
import { MAX_ATTACHMENT_FILE_SIZE } from '~/features/attachment/constants';
import { filesize, getFileExtensionFromMime } from '~/lib/file';
import AddFilesSvg from './addFiles.svg?react';
import { UploadFileStatus } from './hooks';
import UploadStatus from './UploadStatus';

export interface FileUploaderProps<ResponseBody, RequestBody> {
  allowedFileMimeTypes?: string[];
  maxSizeForSingleFile?: number;
  onUpload: (file: File) => void;
  uploadFileStatus: UploadFileStatus<ResponseBody, RequestBody>[];
  onReview?: (id: string) => void;
  onRemove?: (id: string) => void;
  onRetry?: (id: string) => void;
  onSubmit?: () => void;
}

const Container = styled(Box)`
  display: flex;
  flex-direction: column;
  user-select: none;
  padding: ${({ theme }) => theme.spacing(2)};
  background-color: ${grey[50]};
  border-radius: ${({ theme }) => theme.shape.borderRadius}px;
`;

const DropZone = styled<React.FC<BoxProps & { dragActive: boolean }>>(Box)`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  border: 2px dashed ${grey[400]};
  border-radius: calc(${({ theme }) => theme.shape.borderRadius}px * 0.6);
  padding: ${({ theme }) => theme.spacing(4)};
  transition: 200ms;

  ${(props) =>
    props.dragActive &&
    css`
      border-color: ${props.theme.palette.primary.main};
      background-color: ${lighten(props.theme.palette.primary.main, 0.95)};
    `}
`;

const AddFilesIcon = styled(AddFilesSvg)`
  height: 5.5rem;
`;

const MainText = styled(Typography)`
  display: flex;
  align-items: center;
  white-space: pre-wrap;
  margin-top: ${({ theme }) => theme.spacing(2)};

  &,
  & > .${linkClasses.root} {
    font-weight: 500;
  }
`;

const SecondaryText = styled(Typography)`
  color: ${({ theme }) => theme.palette.text.secondary};
  margin-top: ${({ theme }) => theme.spacing(1)};
  font-size: 0.8rem;
  font-weight: 500;
  text-align: center;
  white-space: pre-wrap;
`;

const UploadStatusContainer = styled(Box)`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  margin-top: ${({ theme }) => theme.spacing(1)};

  &:empty {
    margin-top: 0;
  }
`;

const SubmitButton = styled(Button)`
  margin-top: ${({ theme }) => theme.spacing(1)};
`;

const buildAcceptObject = (mimeTypes: string[]): Accept => {
  return mimeTypes.reduce(
    (res, mime) => ({
      ...res,
      [mime]: [`.${getFileExtensionFromMime(mime)}`]
    }),
    {}
  );
};

function FileUploader<ResponseBody, RequestBody>({
  allowedFileMimeTypes,
  maxSizeForSingleFile,
  onUpload,
  uploadFileStatus,
  onReview,
  onRemove,
  onRetry,
  onSubmit
}: FileUploaderProps<ResponseBody, RequestBody>): ReactElement {
  const onDrop = useMemoizedFn((files: File[]) => files.forEach(onUpload));
  const validateFile = (file: File) => {
    if (file.size > MAX_ATTACHMENT_FILE_SIZE) {
      return {
        code: ErrorCode.FileTooLarge,
        message: `The file '${file.name}' is too large for the server to process.`
      };
    }
    return null;
  };
  const accept = allowedFileMimeTypes ? buildAcceptObject(allowedFileMimeTypes) : undefined;
  const { getRootProps, getInputProps, isDragActive, fileRejections } = useDropzone({
    onDrop,
    accept,
    validator: validateFile
  });
  useEffect(() => {
    fileRejections.forEach(({ errors }) => {
      errors.forEach((error) => {
        toast.error(error.message);
      });
    });
  }, [fileRejections]);

  const submitDisabled = uploadFileStatus.every((status) => !status.done);
  const succeedFilesCount = uploadFileStatus.filter((status) => status.done).length;

  return (
    <Container>
      <DropZone {...getRootProps()} dragActive={isDragActive}>
        <input {...getInputProps()} hidden />
        <AddFilesIcon />
        <MainText variant="body2">
          Drag & Drop file(s) here or{' '}
          <Link underline="none" component="button" variant="body2">
            browse
          </Link>
          .
        </MainText>
        <SecondaryText>
          {maxSizeForSingleFile !== undefined && `Max. File Size ${filesize(maxSizeForSingleFile)}`}
          {maxSizeForSingleFile !== undefined && allowedFileMimeTypes && '\n'}
          {allowedFileMimeTypes &&
            `Supports: ${allowedFileMimeTypes.map((mime) => `.${getFileExtensionFromMime(mime)}`).join(', ')}`}
        </SecondaryText>
      </DropZone>
      <UploadStatusContainer>
        {uploadFileStatus.map((status) => (
          <UploadStatus
            key={status.id}
            id={status.id}
            filename={status.filename}
            filetype={status.filetype}
            size={status.filesize}
            progress={status.progress}
            done={status.done}
            onReview={onReview}
            onRemove={onRemove}
            onRetry={onRetry}
            error={status.error?.message}
          />
        ))}
      </UploadStatusContainer>
      {onSubmit && !submitDisabled && <SubmitButton onClick={onSubmit}>Submit ({succeedFilesCount})</SubmitButton>}
    </Container>
  );
}

export default FileUploader;
